/**
 * JobDetailContainer
 *
 * 책임:
 * - useJobDetail 훅으로 Job 상세 + 액션 관리
 * - "원본 스냅샷" 버튼 → snapshotUrl() → 새 탭에서 열기
 * - "재시도" 버튼 → retryJob() → 새 Job ID 반환 → 토스트
 * - 로딩/에러 상태 표시
 * - API의 JobDetailDto를 화면의 JobDetailData로 변환 (undefined → null)
 * - JobDetailScreen에 job + 콜백 전달
 *
 * AdminApp에서 screen === 'job-detail' 일 때 (jobId prop과 함께) 렌더링
 */

import { useContext } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useJobDetail } from '../hooks/useJobDetail';
import { ToastContext } from '../context/ToastContext';
import { JobDetailScreen } from '../screens/JobDetail/JobDetailScreen';
import { LoadingView } from '../../design-system/molecules/LoadingView/LoadingView';
import { useMinimumLoading } from '../hooks/useMinimumLoading';
import type { JobDetailData } from '../types';

export interface JobDetailContainerProps {
  // 상세 조회할 Job ID (null이면 로딩 안 함)
  jobId: number | null;
  // 목록으로 돌아가기 (AdminApp이 네비게이션 처리)
  onBack: () => void;
  // 다른 Job 상세로 이동 (재시도 원본 Job 링크 등). AdminApp이 네비게이션 처리.
  onOpenJob?: (id: number) => void;
}

export function JobDetailContainer({ jobId, onBack, onOpenJob }: JobDetailContainerProps) {
  const { token } = useAuth();
  // useJobDetail: Job 상세 + 스테이지를 30초 주기로 함께 폴링(단일 스냅샷).
  // status·타임라인·StageView가 한 번의 refresh로 동시 갱신된다.
  const { data, stageColumns, stageRuns, snapshotAvailable, loading, error, downloadSnapshot, retryJob } =
    useJobDetail(token, jobId);
  const toast = useContext(ToastContext);

  // "원본 스냅샷" 버튼 클릭: 스냅샷 파일(Blob) 조회 → 브라우저 다운로드 트리거
  const handleDownloadSnapshot = async () => {
    try {
      const { blob, filename } = await downloadSnapshot();
      // Blob → 임시 object URL → 앵커 클릭으로 다운로드 → URL 해제
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast?.show('success', `원본 스냅샷 다운로드: ${filename}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : '스냅샷 다운로드 실패';
      toast?.show('error', message);
    }
  };

  // "재시도" 버튼 클릭: 기존 Job 재시도 → 새 Job ID 반환 → 목록으로 이동
  const handleRetry = async () => {
    try {
      const newJobId = await retryJob();
      toast?.show('success', `재시도 시작됨 (Job #${newJobId})`);
      onBack();
    } catch (err) {
      const message = err instanceof Error ? err.message : '재시도 실패';
      toast?.show('error', message);
    }
  };

  // 로딩 상태 — 실제 로딩이 0.7초보다 빨리 끝나도 최소 0.7초는 로딩 화면을 유지.
  const showLoading = useMinimumLoading(loading);
  if (showLoading) {
    return <LoadingView message="Job 상세 로딩 중…" />;
  }

  // 에러 상태
  if (error) {
    return (
      <div
        style={{
          padding: '2rem',
          color: '#c00',
          backgroundColor: '#fee',
          borderRadius: '0.5rem',
          margin: '2rem',
        }}
      >
        <div>❌ {error}</div>
      </div>
    );
  }

  // 데이터 없음
  if (!data) {
    return null;
  }

  // API JobDetailDto → 화면 JobDetailData 변환
  // 화면은 nullable 필드를 string|null로 기대하므로 undefined를 null로 정규화
  const job: JobDetailData = {
    id: data.id,
    module: data.module,
    trigger: data.trigger,
    collector: data.collector,
    // 폴링된 상세에서 직접 — 배지·타임라인·액션 버튼이 함께 실시간 갱신.
    status: data.status,
    posts: data.posts,
    comments: data.comments,
    collectionId: data.collectionId ?? null,
    created: data.created,
    started: data.started ?? null,
    finished: data.finished ?? null,
    inputJson: data.inputJson,
    errorText: data.errorText ?? null,
    retryOfJobId: data.retryOfJobId ?? null,
    latestRetryId: data.latestRetryId ?? null,
  };

  // JobDetailScreen 렌더링: Job 상세 + 콜백 전달
  return (
    <JobDetailScreen
      job={job}
      onBack={onBack}
      snapshotAvailable={snapshotAvailable}
      onOpenSnapshot={handleDownloadSnapshot}
      onRetry={handleRetry}
      onOpenJob={onOpenJob}
      stageColumns={stageColumns}
      stageRuns={stageRuns}
    />
  );
}
